// src/lib/reponse-json.js — UNE RÉPONSE QUI N'EST PAS DU JSON N'EST JAMAIS ADOPTÉE.
//
// ══ POURQUOI (Dimitri, 2026-09-28) ══════════════════════════════════════════
//
//   « Des écrans vides sans un mot, c'est moi qui prends une décision sur du
//     néant en croyant voir mes vraies données. »
//
// Les quatre clients de blob du navigateur (`cfg-api`, `data-api`,
// `compta-api`, `stock-api`) portaient la MÊME lecture, recopiée quatre fois :
//
//     const json = await res.json().catch(() => ({}))
//     if (!res.ok) throw new Error(json.error || fallback)
//     return json
//
// Elle ne regardait que le CODE HTTP. Une réponse « 200 + page HTML » — la
// page de protection Vercel devant un aperçu, une page d'erreur d'hébergeur,
// un portail captif d'hôtel ou d'aéroport — passait pour un succès : le
// `res.json()` échouait, le `.catch` fabriquait `{}`, et ce `{}` était adopté
// COMME SI C'ÉTAIT LA DONNÉE. Le parc, le calendrier, l'argent, les fiches
// s'ouvraient vides, sans un mot. Silencieux et parfaitement plausible.
//
// ══ CE QUI FAIT FOI : LA FORME DE CE QUI EST REÇU, PAS CE QUI EST ANNONCÉ ═══
//
// Le verdict est rendu sur le CORPS effectivement reçu (se parse-t-il en JSON,
// et est-ce un objet ?), pas sur l'en-tête `Content-Type`. Deux raisons :
//
//   1. `Content-Type` est une DÉCLARATION, le corps est un FAIT. Un portail
//      captif annonce volontiers `application/json` avant de servir sa page de
//      connexion ; se fier à l'annonce, c'est reconduire exactement le défaut
//      qu'on ferme (croire l'enveloppe plutôt que regarder dedans).
//   2. Refuser SUR L'EN-TÊTE SEUL créerait de fausses alertes : un proxy qui
//      renvoie un corps JSON parfaitement valide étiqueté `text/plain` porte
//      une donnée utilisable. On refuserait une lecture saine — et un garde
//      qui crie à tort finit par être ignoré, donc désarmé.
//
// L'en-tête n'est donc jamais consulté : il ne peut ni accepter ni refuser à
// lui seul. Le corps tranche, et lui seul.
//
// ══ LE PIÈGE : `{}` LÉGITIME ≠ `{}` FABRIQUÉ ════════════════════════════════
//
// Un objet vide `{}` peut être une configuration légitimement vide (première
// installation, compta jamais renseignée). On ne peut donc PAS refuser sur
// « l'objet est vide » — ce serait casser la première installation, sans
// aucun moyen de la distinguer d'une panne.
//
// La distinction est pourtant EXACTE, et elle ne tient pas à l'inspection du
// résultat : elle tient au MOMENT où l'on tranche. Un corps HTML ne se parse
// jamais en `{}` — il ne se parse pas du tout. Le `{}` ambigu n'existait que
// parce que le `.catch` en FABRIQUAIT un après l'échec. En rendant le verdict
// AVANT toute valeur de repli, les deux cas se séparent sans ambiguïté :
//
//   • corps qui se parse en objet JSON (`{}` compris) → donnée, on adopte ;
//   • corps qui ne se parse pas → panne, on refuse et on le DIT.
//
// Aucun repli n'est plus fabriqué ici. C'est la règle centrale de ce module.
//
// ⚠️ Côté navigateur UNIQUEMENT. `fbz8-cfg` est sur le chemin Stripe
// (`cfg-reader-server`) : rien de ce lot ne touche au serveur.

/**
 * Le serveur a répondu, mais ce qu'il a répondu n'est pas exploitable.
 *
 * Distinguée d'une `Error` ordinaire pour que l'appelant puisse l'afficher
 * autrement : un 500 dit « ça a raté », une réponse illisible dit « je ne sais
 * même pas à qui j'ai parlé ».
 */
export class ReponseIllisible extends Error {
  constructor(message) {
    super(message);
    this.name = "ReponseIllisible";
    // Marqueur transportable : `instanceof` survit mal aux frontières de
    // bundles et aux ré-emballages d'erreur, un booléen non.
    this.illisible = true;
  }
}

/** Vrai si l'erreur est un refus de réponse illisible, d'où qu'elle vienne. */
export function estIllisible(e) {
  return Boolean(e && e.illisible);
}

/**
 * Lit le corps d'une réponse et REFUSE tout ce qui n'est pas un objet JSON.
 *
 * @param {Response} res réponse `fetch`.
 * @param {string} fallback message à lever si le serveur a répondu en erreur
 *   sans texte exploitable (comportement historique, inchangé).
 * @param {string} messageIllisible phrase à montrer à Dimitri si le corps
 *   n'est pas du JSON. Écrite par l'appelant, qui seul sait de quoi il parle
 *   (« ta configuration », « tes données de facturation »…).
 * @returns {Promise<object>} l'objet JSON reçu — y compris `{}` s'il est
 *   réellement venu du serveur.
 * @throws {Error} sur réponse en erreur (message du serveur ou `fallback`).
 * @throws {ReponseIllisible} sur corps non-JSON, ou JSON qui n'est pas un
 *   objet (tableau, `null`, nombre, chaîne).
 */
export async function lireJsonOuRefuser(res, fallback, messageIllisible) {
  // `res.text()` et non `res.json()` : on veut le corps brut pour pouvoir
  // décider, pas une exception qui efface la preuve.
  let brut = null;
  try {
    brut = await res.text();
  } catch (e) {
    // Corps coupé en route (réseau tombé en plein transfert). Ce n'est pas du
    // JSON non plus, et surtout ce n'est pas un vide légitime.
    brut = null;
  }

  let json;
  let lisible = false;
  if (typeof brut === "string") {
    try {
      json = JSON.parse(brut);
      lisible = true;
    } catch (e) {
      lisible = false;
    }
  }

  // Réponse en erreur : comportement historique STRICTEMENT préservé — le
  // message du serveur s'il est lisible, sinon le fallback de l'appelant. Ces
  // messages-là parlent déjà (409 de conflit, 400 de blob trop lourd…).
  if (!res.ok) {
    const message =
      (lisible && json && typeof json === "object" && json.error) || fallback;
    const erreur = new Error(message);
    // F13 suite (tour 4) : le CODE du serveur voyage avec l'erreur (ajout pur,
    // message inchangé) — data-api y lit « data_ecriture_echouee », le seul
    // 500 où le serveur affirme lui-même que rien n'est enregistré.
    if (
      lisible &&
      json &&
      typeof json === "object" &&
      typeof json.code === "string"
    )
      erreur.code = json.code;
    throw erreur;
  }

  // Succès annoncé + corps inexploitable = le cas qui vidait les écrans.
  if (
    !lisible ||
    json === null ||
    typeof json !== "object" ||
    Array.isArray(json)
  ) {
    throw new ReponseIllisible(messageIllisible);
  }

  return json;
}
